import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, PageLoading } from "@lumika/ui";
import { authApi } from "../api/auth";
import { ApiError } from "../api/client";
import { LumikaLogo } from "@lumika/ui";

/**
 * Shown when Google verified an email that maps to more than one
 * dealership. Reached via a redirect from the login callback that set the
 * short-lived choice cookie; this page reads it (through
 * `/auth/dealership-choices`) and exchanges the picked dealership for a
 * full session, then hard-reloads into the app.
 */
export function SelectDealershipPage() {
  const choicesQuery = useQuery({
    queryKey: ["dealership-choices"],
    queryFn: authApi.dealershipChoices,
    retry: false,
  });

  const select = useMutation({
    mutationFn: (dealershipId: string) => authApi.selectDealership(dealershipId),
    onSuccess: () => window.location.assign("/"),
    onError: (err) => {
      // A 401/403 means the short-lived choice cookie has expired or gone
      // invalid — there is no way forward from this page, so return to
      // sign-in. Other errors (e.g. a transient network blip) fall through
      // to the inline retry alert below.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        window.location.assign("/sign-in");
      }
    },
  });

  // Leave the picker whenever there is nothing to pick: the choice cookie
  // expired/invalid (query errored), or the accessible set shrank to empty
  // since login (query succeeded with []). Either way sign-in is the only
  // sensible destination — never strand the user on a buttonless page.
  useEffect(() => {
    if (choicesQuery.isError) {
      window.location.assign("/sign-in");
    } else if (choicesQuery.isSuccess && choicesQuery.data.length === 0) {
      window.location.assign("/sign-in");
    }
  }, [choicesQuery.isError, choicesQuery.isSuccess, choicesQuery.data]);

  const choices = choicesQuery.data ?? [];

  if (choicesQuery.isLoading || choicesQuery.isError || choices.length === 0) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background text-foreground">
        <PageLoading message="Loading dealerships…" />
      </div>
    );
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-background p-6 text-foreground">
      <section className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <LumikaLogo theme="auto" size={48} />
        <div className="grid gap-2">
          <h1 className="text-4xl font-semibold tracking-tight">Choose a dealership</h1>
          <p className="text-sm text-muted-foreground">
            Your account has access to more than one dealership. Choose where you want to work.
          </p>
        </div>

        {select.isError && (
          <div
            role="alert"
            className="w-full rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-sm"
          >
            Could not open that dealership. Please try again.
          </div>
        )}

        <ul className="flex w-full flex-col gap-2">
          {choices.map((choice) => (
            <li key={choice.dealershipId} className="contents">
              <Button
                type="button"
                variant="outline"
                disabled={select.isPending}
                onClick={() => select.mutate(choice.dealershipId)}
                className="h-auto w-full justify-between px-4 py-3 text-left"
              >
                <span className="truncate">
                  {select.isPending && select.variables === choice.dealershipId
                    ? `Opening ${choice.dealershipName}…`
                    : choice.dealershipName}
                </span>
                <span aria-hidden className="text-muted-foreground">
                  →
                </span>
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
